import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2, AlertTriangle, CheckCircle, XCircle, Package, FileText, ShoppingCart, ArrowRightLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { notificationsAPI } from '../lib/api';
import { formatTimeAgo } from '../lib/timezone';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'approval_requested':
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case 'approval_approved':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'approval_rejected':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'low_stock':
      return <Package className="w-4 h-4 text-orange-500" />;
    case 'import_completed':
      return <FileText className="w-4 h-4 text-blue-500" />;
    case 'po_submitted':
      return <ShoppingCart className="w-4 h-4 text-primary" />;
    case 'po_email_failed':
      return <AlertTriangle className="w-4 h-4 text-rose-500" />;
    case 'transfer_request_submitted':
      return <ArrowRightLeft className="w-4 h-4 text-amber-500" />;
    case 'transfer_request_approved':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'transfer_request_rejected':
      return <XCircle className="w-4 h-4 text-red-500" />;
    default:
      return <Bell className="w-4 h-4 text-muted-foreground" />;
  }
};

export const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const fetchNotificationCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.getCount();
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.list({ limit: 20 });
      setNotifications(res.data);
      setLoadError(false);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotificationCount();
    
    // Poll every 30 seconds - but check if polling is paused
    const interval = setInterval(() => {
      // P0 FIX: Skip poll if paused (modal open or critical operation)
      const { isPollingPaused } = require('../lib/api');
      if (!isPollingPaused()) {
        fetchNotificationCount();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotificationCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const handleMarkAllRead = async () => {
    setMarkingRead(true);
    try {
      await notificationsAPI.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await notificationsAPI.markRead(notification.id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => 
          n.id === notification.id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        ));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Navigate based on notification type
    let metadata = {};
    try {
      metadata = notification.metadata ? JSON.parse(notification.metadata) : {};
    } catch (e) {
      // metadata might already be an object
      metadata = notification.metadata || {};
    }

    setOpen(false);

    switch (notification.type) {
      case 'po_submitted':
        // Navigate to purchase orders list with "All POs" tab active
        navigate('/purchase-orders?tab=all');
        break;
      case 'po_email_failed':
        navigate('/purchase-orders?tab=all&emailIssues=1');
        break;
      case 'approval_requested':
      case 'approval_approved':
      case 'approval_rejected':
        navigate('/approvals');
        break;
      case 'transfer_request_submitted':
      case 'transfer_request_approved':
      case 'transfer_request_rejected':
        navigate('/approvals');
        break;
      case 'low_stock':
        navigate('/replenishment?view=needs-attention');
        break;
      default:
        // No navigation for unknown types
        break;
    }
  };

  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-white hover:bg-white/10 hover:text-white"
          data-testid="notifications-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-destructive text-[10px] font-bold text-white rounded-full flex items-center justify-center px-1">
              {displayCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-7"
              onClick={handleMarkAllRead}
              disabled={markingRead}
            >
              {markingRead ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <CheckCheck className="w-3 h-3 mr-1" />
              )}
              Mark all read
            </Button>
          )}
        </div>
        
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm" data-testid="notifications-empty-or-error">{loadError ? 'Could not load notifications. Try again shortly.' : 'No notifications yet'}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${
                    !notification.is_read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.is_read ? 'font-medium' : 'text-foreground'}`}>
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="shrink-0">
                        <div className="w-2 h-2 bg-primary rounded-full" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
